import { Injectable } from '@angular/core';

import { Observable, filter, map, tap } from 'rxjs';

import { CamBaseService, GraphEndpoint, HandleComplexRequest, HandleSimpleRequest } from '@camelot/server';
import { isNonNullable } from '@camelot/utils';

import { Invoice, invoiceProps } from './dto/invoice';
import { InvoiceStatus } from './dto/invoice-status';
import { PaymentLink } from './dto/payment-link';
import { progressStatementsProps } from './dto/progress-statements';
import { READ_INVOICE } from './mutations';
import { GET_INVOICES, GET_INVOICE_TENANT_ROUTE, GET_PAYMENT_LINK, GET_PAYMENT_LINKS } from './queries';

const graphEndpoint: GraphEndpoint = {
  clientName: 'invoiceService',
  endpoint: 'invoice',
};

@Injectable({
  providedIn: 'root',
})
export class CamInvoicesService extends CamBaseService {
  protected _graphEndpoint = graphEndpoint;

  public invoices = new HandleSimpleRequest<Invoice[]>();
  public invoice = new HandleComplexRequest<Invoice>();
  public invoiceContext = new HandleComplexRequest<Invoice>();
  public invoiceContextInfo = new HandleComplexRequest<Invoice>();
  public invoicesByProject = new HandleComplexRequest<Invoice[]>();
  public invoicesByContact = new HandleComplexRequest<Invoice[]>();
  public invoicesLight = new HandleSimpleRequest<Invoice[]>();
  public invoicesLightByContact = new HandleComplexRequest<Invoice[]>();

  public tenantRoute = new HandleSimpleRequest<string>();

  public paymentLink = new HandleComplexRequest<PaymentLink | null>();

  constructor() {
    super();
    super.registerRoutes({ graphEndpoint: graphEndpoint });
  }

  public fetchInvoices$() {
    return this.invoices.fetch(
      this._queryInvoices$(
        ``,
        `
            ${invoiceProps.get('id')}
            ${invoiceProps.get('reference')}
            ${invoiceProps.get('inclVatTotal')}
            ${invoiceProps.get('exclVatTotal')}
            ${invoiceProps.get('paymentStatus')}
            ${invoiceProps.get('isCreditNote')}
            ${invoiceProps.get('dueDate')}
            ${invoiceProps.get('validationDate')}
            ${invoiceProps.get('beneficiaryIban')}
            ${invoiceProps.get('beneficiaryBic')}
            ${invoiceProps.get('beneficiaryName')}
            ${invoiceProps.get('structuredCommunication')}
            ${invoiceProps.get('isNew')}
            ${invoiceProps.get('directDebitStatus')}
            ${invoiceProps.get('directDebitStatusDate')}
          `
      )
    );
  }

  public fetchPurchaseInvoices$(isPurchase: boolean) {
    return this.invoices.fetch(
      this._queryInvoices$(
        `where: { isPurchase: { eq: ${isPurchase} } }`,
        `
            ${invoiceProps.get('id')}
            ${invoiceProps.get('reference')}
            ${invoiceProps.get('inclVatTotal')}
            ${invoiceProps.get('exclVatTotal')}
            ${invoiceProps.get('paymentStatus')}
            ${invoiceProps.get('isCreditNote')}
            ${invoiceProps.get('dueDate')}
            ${invoiceProps.get('beneficiaryIban')}
            ${invoiceProps.get('beneficiaryBic')}
            ${invoiceProps.get('beneficiaryName')}
            ${invoiceProps.get('structuredCommunication')}
            ${invoiceProps.get('isNew')}
          `
      )
    );
  }

  public fetchInvoicesByContact$(contactId: string) {
    return this.invoicesByContact.fetch(
      contactId,
      this._queryInvoices$(
        `where: { contactId: { eq: "${contactId}" } }`,
        `
            ${invoiceProps.get('id')}
            ${invoiceProps.get('reference')}
            ${invoiceProps.get('isCreditNote')}
            ${invoiceProps.get('status')}
            ${invoiceProps.get('paymentStatus')}
            ${invoiceProps.get('validationDate')}
            ${invoiceProps.get('contactId')}
            ${invoiceProps.get('projectId')}
          `
      )
    );
  }

  public fetchInvoicesLight$() {
    return this.invoicesLight.fetch(
      this._queryInvoices$(
        ``,
        `
            ${invoiceProps.get('id')}
            ${invoiceProps.get('reference')}
          `
      )
    );
  }

  public fetchInvoicesLightByContact$(contactId: string) {
    return this.invoicesLightByContact.fetch(
      contactId,
      this._queryInvoices$(
        `where: { contactId: { eq: "${contactId}" } }`,
        `
            ${invoiceProps.get('id')}
            ${invoiceProps.get('reference')}
          `
      )
    );
  }

  public fetchInvoicesByProject$(projectId: string) {
    return this.invoicesByProject.fetch(
      projectId,
      this._queryInvoices$(
        `where: { projectId: { eq: "${projectId}" }}`,
        `
            ${invoiceProps.get('id')}
            ${invoiceProps.get('reference')}
            ${invoiceProps.get('inclVatTotal')}
            ${invoiceProps.get('exclVatTotal')}
            ${invoiceProps.get('paymentStatus')}
            ${invoiceProps.get('isCreditNote')}
            ${invoiceProps.get('dueDate')}
            ${invoiceProps.get('beneficiaryIban')}
            ${invoiceProps.get('beneficiaryBic')}
            ${invoiceProps.get('beneficiaryName')}
            ${invoiceProps.get('structuredCommunication')}
            ${invoiceProps.get('isNew')}
            `
      )
    );
  }

  public fetchInvoice$(id: string) {
    return this.invoice.fetch(
      id,
      this._queryInvoice$(
        `where: { id: { eq: "${id}" }}`,
        `
        ${invoiceProps.get('id')}
        ${invoiceProps.get('reference')}
        ${invoiceProps.get('description')}
        ${invoiceProps.get('downloadUriPDF')}
        ${invoiceProps.get('rowsCount')}
        ${invoiceProps.get('inclVatTotal')}
        ${invoiceProps.get('exclVatTotal')}
        ${invoiceProps.get('dueDate')}
        ${invoiceProps.get('validationDate')}
        ${invoiceProps.get('invoiceNumber')}
        ${invoiceProps.get('paymentStatus')}
        ${invoiceProps.get('contactId')}
        ${invoiceProps.get('projectId')}
        ${invoiceProps.get('hiddenDetails')}
        ${invoiceProps.get('isNew')}
        ${invoiceProps.get('isCreditNote')}
        ${invoiceProps.get('progressStatements')} {
          ${progressStatementsProps.get('id')}
          ${progressStatementsProps.get('quotationVersionId')}
          ${progressStatementsProps.get('isFinal')}
          ${progressStatementsProps.get('isDeposit')}
        }
        ${invoiceProps.get('beneficiaryIban')}
        ${invoiceProps.get('beneficiaryBic')}
        ${invoiceProps.get('beneficiaryName')}
        ${invoiceProps.get('structuredCommunication')}
        ${invoiceProps.get('directDebitStatus')}
        ${invoiceProps.get('directDebitStatusDate')}
        `
      ).pipe(tap(invoice => this.isRead$(invoice.id).subscribe()))
    );
  }

  public fetchInvoiceContext$(id: string) {
    return this.invoiceContext.fetch(
      id,
      this._queryInvoice$(
        `where: { id: { eq: "${id}" }}`,
        `
        ${invoiceProps.get('id')}
        ${invoiceProps.get('reference')}
        ${invoiceProps.get('description')}
        ${invoiceProps.get('downloadUriPDF')}
        ${invoiceProps.get('inclVatTotal')}
        ${invoiceProps.get('exclVatTotal')}
        ${invoiceProps.get('dueDate')}
        ${invoiceProps.get('validationDate')}
        ${invoiceProps.get('paymentStatus')}
        ${invoiceProps.get('projectId')}
        `
      )
    );
  }

  public fetchInvoiceContextInfo$(id: string) {
    return this.invoiceContextInfo.fetch(
      id,
      this._queryInvoice$(
        `where: { and: [{ id: { eq: "${id}" } }, { status: { neq: ${InvoiceStatus.Draft} } }] }`,
        `
        ${invoiceProps.get('id')}
        ${invoiceProps.get('reference')}
        ${invoiceProps.get('paymentStatus')}
        ${invoiceProps.get('isCreditNote')}
        ${invoiceProps.get('validationDate')}
        ${invoiceProps.get('exclVatTotal')}
        ${invoiceProps.get('inclVatTotal')}
        ${invoiceProps.get('dueDate')}
        ${invoiceProps.get('downloadUriPDF')}
        `
      )
    );
  }

  private _queryInvoice$(where: string, props: string): Observable<Invoice> {
    return this._graphService
      .fetchPagedQueryList<Invoice>(GET_INVOICES(where, props), 'invoices', graphEndpoint.clientName)
      .pipe(
        map(data => (data.items && data.items.length === 1 ? data.items[0] : null)),
        filter(isNonNullable)
      );
  }

  private _queryInvoices$(where: string, props: string): Observable<Invoice[]> {
    return this._graphService
      .fetchPagedQueryList<Invoice>(GET_INVOICES(where, props), 'invoices', graphEndpoint.clientName)
      .pipe(map(data => data.items ?? []));
  }

  public isRead$(id: string) {
    return this._graphService.mutateBuilder<boolean>(READ_INVOICE(id), graphEndpoint.clientName);
  }

  public getPaymentLink$(
    id: string,
    qrCode: boolean,
    confirmationUrl: string,
    cancelUrl: string,
    errorUrl: string
  ): Observable<PaymentLink | null> {
    return this.paymentLink.fetch(
      id,
      this._graphService.fetchQuery<PaymentLink | null>(
        GET_PAYMENT_LINK(id, qrCode, confirmationUrl, cancelUrl, errorUrl),
        'paymentLink',
        graphEndpoint.clientName
      )
    );
  }

  public getPaymentLinks$(
    ids: string[],
    qrCode: boolean,
    confirmationUrl: string,
    cancelUrl: string,
    errorUrl: string
  ): Observable<PaymentLink[]> {
    return this._graphService
      .fetchQueryBuilder<PaymentLink[]>(
        GET_PAYMENT_LINKS(ids, qrCode, confirmationUrl, cancelUrl, errorUrl),
        graphEndpoint.clientName
      )
      .pipe(
        map(paymentLinks => paymentLinks ?? []),
        tap(paymentLinks =>
          paymentLinks.forEach(paymentLink => this.paymentLink.add(paymentLink.invoiceId, paymentLink))
        )
      );
  }

  public getInvoicesLightInfo$(ids: string[]) {
    return this._queryInvoices$(
      `where: { id: { in: [${ids.map(id => `"${id}"`).join(', ')}] } }`,
      `
            ${invoiceProps.get('id')}
            ${invoiceProps.get('reference')}
          `
    );
  }

  public fetchTenantRoute$(invoiceId: string) {
    return this.tenantRoute.fetch(
      this._graphService
        .fetchQuery<string>(GET_INVOICE_TENANT_ROUTE(invoiceId), 'invoiceTenantRoute', graphEndpoint.clientName)
        .pipe(filter(isNonNullable))
    );
  }
}
